import { defineStore } from 'pinia';
import { useStoreRoot } from '~/stores/abstract/root';
import { createPopulateString, parseCollectionData } from '~/utils/strapiComponents/addStrapiData';
import { filterDateActiveItem, processBannerRules } from '~/utils/strapiComponents/filters';
import { strapi } from '../transport/strapi';
import type {
  IBannerRules,
  IMainPageBannersData,
  IMainPageBannersStrapiResponse,
} from '~/types/banners';

export const useStoreMainpageBanners = defineStore('mainpageBanners', {
  state: () => ({
    banners: [] as IMainPageBannersData[],
    requestPromise: null as Promise<IMainPageBannersStrapiResponse> | null,
  }),

  getters: {
    activeBanners(state): IMainPageBannersData[] {
      const storeSsPlayer = useStoreRoot();

      return (
        state.banners
          // get banners with selected language
          .filter((banner) => banner.locale === storeSsPlayer.userLang)
          .filter(filterDateActiveItem)
      );
    },
    personalList(): IMainPageBannersData[] {
      return this.activeBanners.filter((banner): boolean =>
        banner.bannerRules?.length ? processBannerRules(banner.bannerRules as IBannerRules[]) : true
      );
    },
  },

  actions: {
    async loadMainPageBanners(force = false) {
      if (!force && this.banners.length > 0) {
        return true;
      }

      if (!this.requestPromise) {
        const populateString = createPopulateString([
          'banner',
          'banner.desktopAssetFront',
          'banner.desktopAssetBack',
          'bannerRules',
          'bannerAction',
          'bannerAction.params',
          'bannerImage',
        ]);

        this.requestPromise = strapi()
          .get('mainpage-stepper-banners', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = parseCollectionData(response.data);
        const filteredState = this.banners.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.banners = [...filteredState, ...parsedData].sort(
          (a, b) => Number(a.position) - Number(b.position)
        );

        return true;
      } else {
        return false;
      }
    },
  },
});
